import {
  Box,
  Button,
  Grid2,
  Typography,
  useTheme,
  TextField,
} from "@mui/material";
import { useStyles } from "./styles";
import clsx from "clsx";
import PropTypes from "prop-types";
import TransferList from "components/transferList";
import { useLocation, useNavigate } from "react-router-dom";
import { createRef, useState, useEffect } from "react";
import constants from "constants/constants.json";
import Notification from "components/snackbar";
import { getToken } from "utils/loadAuth";
import { useAuth0 } from "@auth0/auth0-react";
import {
  getRoleById,
  createExternalRole,
  fetchAllExternalPermissions,
  updateExternalRole,
} from "pages/userManagement/roles/roles.services";
import TransferListLoading from "components/transferList/transferListLoading";

const RolesSettings = ({ isNavbarOpen }) => {
  const theme = useTheme();
  const classes = useStyles({ theme, isNavbarOpen });
  const navigate = useNavigate();
  const location = useLocation();
  const roleId = location.state ? location.state.roleId : null;
  const isEdit = Boolean(roleId);
  const { getAccessTokenSilently, loginWithRedirect, user } = useAuth0();

  const [isRoleSaving, setIsRoleSaving] = useState(false);
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [roleName, setRoleName] = useState("");
  const [roleDesc, setRoleDesc] = useState("");
  const [isRoleFetching, setIsRoleFetching] = useState(false);
  const [rolesPermissionMap, setRolesPermissionMap] = useState(new Map());
  const [assignedPermissions, setAssignedPermissions] = useState([]);
  const scrollRef = createRef();
  const userId = location.state ? location.state.userId : undefined;
  const [isPermissionLoading, setIsPermissionLoading] = useState(false);
  const [availablePermissions, setAvailablePermissions] = useState([]);
  const [openNotification, setOpenNotification] = useState(false);
  const [message, setMessage] = useState("");
  const [severity, setSeverity] = useState("info");

  const navigateBack = () => {
    if (userId) {
      return navigate(`/usermanagement/users?userdetails=${userId}`);
    }
    return navigate("/usermanagement/roles");
  };

  const saveNewRole = async (token) => {
    try {
      const payload = {
        userEmail: user.email,
        role: {
          roleName,
          roleDesc,
        },

        rolePermissions: selectedPermissions.map(({ value: permission }) => {
          return {
            permissionId: rolesPermissionMap.get(permission),
            permissionAssigned: permission,
          };
        }),
      };

      await createExternalRole({
        token,
        ...payload,
      });

      setOpenNotification(true);
      setMessage("Role created successfully");
      setSeverity("success");
    } catch (error) {
      setOpenNotification(true);
      setMessage(constants.errorMessage);
      setSeverity("error");
      console.error("Error creating role:", error);
    }
  };

  const updateRolesAndPermissions = async (token) => {
    try {
      const payload = {
        role: {
          roleId: roleId,
          roleName,
          roleDesc,
        },
      };

      const rolePermissions = selectedPermissions.map(
        ({ value: permission }) => {
          return {
            permissionId: rolesPermissionMap.get(permission),
            permissionAssigned: permission,
          };
        }
      );

      payload.rolePermissions = rolePermissions;

      await updateExternalRole({ token, ...payload });

      setOpenNotification(true);
      setMessage("Role updated successfully");
      setSeverity("success");
    } catch (error) {
      setOpenNotification(true);
      setMessage("Error updating role");
      setSeverity("error");
      console.error("Error updating role:", error);
    }
  };

  const handleSave = async () => {
    try {
      setIsRoleSaving(true);
      const token = await getToken({
        getAccessTokenSilently,
        loginWithRedirect,
      });
      if (!token) {
        throw new Error("No token found");
      }
      if (isEdit) {
        await updateRolesAndPermissions(token);
      } else {
        await saveNewRole(token);
      }
    } catch (error) {
      setOpenNotification(true);
      setMessage("Error saving role details");
      setSeverity("error");
      console.error("Error saving role details:", error);
    } finally {
      setIsRoleSaving(false);
    }
  };

  useEffect(() => {
    // fetch role details if role id is present
    const fetchRoleDetails = async () => {
      if (roleId) {
        setIsRoleFetching(true);
        try {
          const token = await getToken({
            getAccessTokenSilently,
            loginWithRedirect,
          });
          if (!token) {
            throw new Error("No token found");
          }
          const res = await getRoleById({ token, roleId: roleId });

          const { role: roleDetails, permissions } = res;
          setRoleName(roleDetails.roleName || "");
          setRoleDesc(roleDetails.roleDesc || "");

          // create a map of permission scope to id for easy lookup
          const permissionMap = new Map();
          permissions.forEach((perm) => {
            permissionMap.set(perm.permissionAssigned, perm.id);
          });
          setRolesPermissionMap(permissionMap);

          const perms = permissions.map((perm) => {
            return {
              value: perm.permissionAssigned,
              disabled: false,
            };
          });

          setAssignedPermissions(perms);
          return perms;
        } catch (error) {
          console.error("Error fetching role details:", error);
          setOpenNotification(true);
        } finally {
          setIsRoleFetching(false);
        }
      }
    };

    const fetchAllPermissions = async (assignedPerms) => {
      try {
        setIsPermissionLoading(true);
        const token = await getToken({
          getAccessTokenSilently,
          loginWithRedirect,
        });
        if (!token) {
          throw new Error("No token found");
        }
        const permissions = await fetchAllExternalPermissions({
          token,
        });
        const permissionMap = new Map();
        permissions.forEach((perm) => {
          permissionMap.set(perm.scope, perm.id);
        });
        const assignedPermIds = assignedPerms
          ? assignedPerms.map((p) => p.value)
          : [];
        setRolesPermissionMap(permissionMap);
        setAvailablePermissions(
          permissions
            .filter((perm) => !assignedPermIds.includes(perm.scope))
            .map((perm) => ({
              value: perm.scope,
              disabled: perm.disabled,
            }))
        );
      } catch (error) {
        console.error("Error fetching all permissions:", error);
      } finally {
        setIsPermissionLoading(false);
      }
    };

    fetchRoleDetails().then((assignedPerms) => {
      fetchAllPermissions(assignedPerms);
    });
  }, [roleId, getAccessTokenSilently, loginWithRedirect]);

  return (
    <Grid2 container className={classes.wrapper}>
      <Box className={classes.viewContainer} ref={scrollRef}>
        <Box className={classes.titleWrapper}>
          <Typography
            data-testid="popupTitle"
            className={classes.title}
            variant="h5"
            align="center"
          >
            {isEdit
              ? constants.updateRoleDetailsTitle
              : constants.createRoleTitle}
          </Typography>
          <Box className={classes.actionWrapper}>
            <Button
              disabled={
                roleName === "" ||
                roleDesc === "" ||
                isRoleSaving ||
                isRoleFetching ||
                isPermissionLoading
              }
              className={classes.saveBtn}
              data-testid="save-role-btn"
              onClick={handleSave}
            >
              {isEdit ? constants.update : constants.save}
            </Button>
            <Button
              className={classes.back}
              disabled={isRoleSaving}
              data-testid="back-btn"
              onClick={() => {
                navigateBack();
              }}
            >
              {constants.back}
            </Button>
          </Box>
        </Box>
        <Grid2 container className={classes.pageWrapper}>
          <Typography
            data-testid="transfer-list"
            className={clsx(classes.text, classes.titles)}
            variant="subtitle1"
          >
            {constants.roleDetails}
          </Typography>
          <Grid2
            container
            justifyContent={"space-between"}
            flex={1}
            flexDirection={"row"}
            sx={{ marginBottom: "1rem" }}
          >
            <Grid2 className={classes.inputWrapper}>
              <TextField
                disabled={isRoleFetching || isRoleSaving}
                placeholder={constants.roleName}
                variant="outlined"
                value={roleName}
                onChange={(e) => setRoleName(e.target.value)}
                fullWidth
                id="role-name-input"
                data-testid="role-name-input"
                className={classes.inputText}
                slotProps={{
                  inputLabel: {
                    style: { color: theme.palette.text.primary },
                    shrink: false,
                  },
                }}
              />
            </Grid2>
            <Grid2 className={classes.inputWrapper}>
              <TextField
                multiline
                disabled={isRoleFetching || isRoleSaving}
                minRows={1}
                maxRows={4}
                placeholder={constants.roleDescription}
                variant="outlined"
                value={roleDesc}
                onChange={(e) => setRoleDesc(e.target.value)}
                fullWidth
                id="role-desc-input"
                className={classes.inputText}
                data-testid="role-desc-input"
                slotProps={{
                  inputLabel: {
                    style: { color: theme.palette.text.primary },
                    shrink: false,
                  },
                }}
              />
            </Grid2>
          </Grid2>

          <Grid2 xs={12} className={classes.transferSection}>
            <Box className={classes.titleWrapper}>
              <Typography
                data-testid="transfer-list"
                className={clsx(classes.text, classes.titles)}
                variant="subtitle1"
              >
                {constants.permissionsTitle}
              </Typography>
              <Box className={classes.actionWrapper}>
                <Button
                  className={classes.saveBtn}
                  sx={{
                    width: "190px!important",
                  }}
                  data-testid="add-permission-btn"
                  onClick={() => {
                    navigate("/usermanagement/permissions/addpermission", {
                      state: {
                        backTo: isEdit
                          ? `/usermanagement/roles/editrole?roleId=${roleId}`
                          : `/usermanagement/roles/addrole`,
                        activeTab: "permissions",
                        roleId: roleId,
                      },
                    });
                  }}
                >
                  {constants.addPermission}
                </Button>
              </Box>
            </Box>

            {isPermissionLoading || isRoleFetching ? (
              <TransferListLoading isNavbarOpen={isNavbarOpen} />
            ) : (
              <TransferList
                loading={isPermissionLoading}
                isNavbarOpen={isNavbarOpen}
                choicesTitle={constants.availablePermissions}
                chosenTitle={constants.assignedPermissions}
                availableList={availablePermissions}
                assignedList={assignedPermissions}
                setSelectedItems={(items) => setSelectedPermissions(items)}
              />
            )}
          </Grid2>
        </Grid2>
        <Notification
          message={message}
          open={openNotification}
          setOpen={setOpenNotification}
          severity={severity}
        />
      </Box>
    </Grid2>
  );
};

RolesSettings.propTypes = {
  handleClose: PropTypes.func,
  role: PropTypes.any,
  isNavbarOpen: PropTypes.bool,
};
export default RolesSettings;
